/**
 * Pure gameplay targets shared by level generation, runtime systems, and tests.
 * Keeping these values together makes the intended difficulty curve auditable.
 */

export interface LevelFlowTarget {
  durationSeconds: number;
  shieldHp: number;
  finaleCombatSeconds: number;
  assumedHitRate: number;
}

export const FINALE_PRESENTATION = {
  /** Clears the 108px HUD/progress zone before the target becomes hittable. */
  spawnY: 155,
  /** Slow advance preserves urgency while guaranteeing a readable duel. */
  speedMultiplier: 0.42,
} as const;

const LEVEL_FLOW_TARGETS: readonly LevelFlowTarget[] = [
  { durationSeconds: 38, shieldHp: 5, finaleCombatSeconds: 4.4, assumedHitRate: 0.14 },
  { durationSeconds: 41, shieldHp: 6, finaleCombatSeconds: 4.5, assumedHitRate: 0.145 },
  { durationSeconds: 43, shieldHp: 7, finaleCombatSeconds: 4.6, assumedHitRate: 0.15 },
  { durationSeconds: 45, shieldHp: 8, finaleCombatSeconds: 4.7, assumedHitRate: 0.155 },
  { durationSeconds: 47, shieldHp: 9, finaleCombatSeconds: 4.8, assumedHitRate: 0.16 },
  { durationSeconds: 49, shieldHp: 10, finaleCombatSeconds: 4.9, assumedHitRate: 0.165 },
  { durationSeconds: 51, shieldHp: 11, finaleCombatSeconds: 5, assumedHitRate: 0.17 },
  { durationSeconds: 53, shieldHp: 12, finaleCombatSeconds: 5.1, assumedHitRate: 0.175 },
  { durationSeconds: 55, shieldHp: 13, finaleCombatSeconds: 5.2, assumedHitRate: 0.18 },
  { durationSeconds: 58, shieldHp: 14, finaleCombatSeconds: 5.4, assumedHitRate: 0.185 },
] as const;

function normalizeLevel(level: number): number {
  return Math.max(1, Math.min(10, Math.round(level)));
}

export function getLevelFlowTarget(level: number): LevelFlowTarget {
  return LEVEL_FLOW_TARGETS[normalizeLevel(level) - 1];
}

export function getTargetDurationSeconds(level: number): number {
  return getLevelFlowTarget(level).durationSeconds;
}

export function getShieldHpForLevel(level: number, tier: number = 1): number {
  const baseHp = getLevelFlowTarget(level).shieldHp;
  const tierBonus = Math.max(0, Math.round(tier) - 1) * 2;
  return baseHp + tierBonus;
}

/**
 * Gates resolve at authored beats. The first appears partway down the track so
 * the one-soldier opening grows within four seconds; every later gate resolves
 * before the final pressure/finale window.
 */
export function getGateActivationTimesSeconds(gateCount: number, durationSeconds: number): number[] {
  const normalizedCount = Math.max(0, Math.round(gateCount));
  const fractionsByCount: Record<number, number[]> = {
    3: [0.1, 0.38, 0.63],
    4: [0.1, 0.29, 0.47, 0.63],
    5: [0.1, 0.23, 0.37, 0.51, 0.65],
  };
  const fractions = fractionsByCount[normalizedCount] ?? Array.from(
    { length: normalizedCount },
    (_unused, index) => 0.1 + (0.65 * index) / Math.max(1, normalizedCount - 1)
  );

  return fractions.map((fraction, index) =>
    index === 0 ? Math.min(3.8, durationSeconds * fraction) : durationSeconds * fraction
  );
}

/**
 * Finale HP uses effective DPS instead of raw bullet capacity. The assumed hit
 * rate reflects straight-line lane shooting, where most bullets are expected
 * to miss unless the player deliberately lines up the squad.
 */
export function getFinaleHp(level: number, bestPathArmy: number, tier: number = 1): number {
  const normalizedLevel = normalizeLevel(level);
  const flow = getLevelFlowTarget(normalizedLevel);
  const expectedSingleTargetDamageRate = normalizedLevel <= 3
    ? 4.5
    : normalizedLevel <= 7
      ? 7
      : 1.25 * 3;
  const effectiveDps =
    Math.max(1, bestPathArmy) * expectedSingleTargetDamageRate * flow.assumedHitRate;
  const baseHp = 14 + normalizedLevel * 4;
  const tierMultiplier = 1 + Math.max(0, Math.round(tier) - 1) * 0.35;
  return Math.max(
    36,
    Math.min(600, Math.round((baseHp + effectiveDps * flow.finaleCombatSeconds) * tierMultiplier))
  );
}

export function getFinaleSpawnTimeSeconds(durationSeconds: number): number {
  return Math.max(1, durationSeconds - 9);
}

export function getWeaponBoxLatestSpawnTimeSeconds(durationSeconds: number): number {
  // A missed box must naturally scroll offscreen before the finale begins.
  return Math.max(8, durationSeconds - 21);
}
